/**
 * DAL'ın rapor kaynağının (Octo.Dal/Services/OctoReportStorageWebExtension.cs) beklediği
 * `reportUrl` biçimi: `{reportActionId}|{parametreJson}`.
 *
 * Boru işaretinden sonrası, rapor parametrelerini taşıyan bir JSON nesnesidir; sunucu onu
 * `Dictionary<string, object>` olarak çözer ve raporun aynı adlı parametrelerine yazar.
 * Parametre yoksa boru işareti de yazılmaz — sunucu tarafı `Split('|', 2)` yaptığı için
 * boş bir kuyruk zararsız olurdu, ama URL'i gereksiz uzatmanın anlamı yok.
 */

/** Rapor parametreleri: ad → değer. Çoklu değerli parametreler dizi olarak verilir. */
export type ReportParameters = Record<string, unknown>;

/**
 * `{reportActionId}` ya da `{reportActionId}|{json}` üretir.
 *
 * `parameters` hazır bir JSON metni olabilir (aksiyon akışında `buildReportFilterValue`
 * öyle üretir) ya da nesne. Boş metin ve boş nesne kuyruk yazdırmaz.
 */
export function buildReportUrl(
  reportActionId: string,
  parameters?: ReportParameters | string | null,
): string {
  const id = (reportActionId ?? '').trim();
  if (!id) return '';
  if (!parameters) return id;

  if (typeof parameters === 'string') {
    const raw = parameters.trim();
    if (!raw || raw === '{}') return id;
    return `${id}|${raw}`;
  }

  const entries = Object.entries(parameters).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return id;
  return `${id}|${JSON.stringify(Object.fromEntries(entries))}`;
}

/**
 * Blazor `GridComponent` rapor aksiyonlarındaki kaynak→hedef alan eşlemesinin karşılığı:
 * seçili satırdaki `sourceFilterFieldName` değeri, raporun `targetFilterFieldName`
 * parametresine geçer. İkisinden biri tanımsızsa parametre üretilmez.
 */
export function reportParametersFromRow(
  row: Record<string, unknown> | null | undefined,
  sourceFilterFieldName: string | null | undefined,
  targetFilterFieldName: string | null | undefined,
): ReportParameters | null {
  if (!row || !sourceFilterFieldName || !targetFilterFieldName) return null;
  const value = row[sourceFilterFieldName];
  if (value === undefined || value === null) return null;
  return { [targetFilterFieldName]: value };
}
